import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Tag,
  Avatar,
  Select,
  Input,
  message,
  Spin,
  Tooltip,
} from 'antd';
import {
  PhoneOutlined,
  UserOutlined,
  DollarOutlined,
  UserAddOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  SoundOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DownOutlined,
  LinkOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useCallCenter } from '../../contexts/CallCenterContext';
import { callCenterOutcomesApi, callCenterCallsApi } from '../../utils/api';
import { toJalali } from '../../utils/dateUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { TextArea } = Input;

const TYPE_META = {
  client: { label: 'مشتری', color: 'green', icon: <UserOutlined />, path: (id) => `/clients/${id}` },
  lead: { label: 'سرنخ', color: 'orange', icon: <UserAddOutlined />, path: (id) => `/leads/${id}` },
  deal: { label: 'معامله', color: 'blue', icon: <DollarOutlined />, path: (id) => `/deals/${id}` },
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '۰ ثانیه';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs} ثانیه`;
  return `${mins} دقیقه${secs > 0 ? ` و ${secs} ثانیه` : ''}`;
};

const formatCurrency = (amount, symbol = 'تومان') => {
  if (amount == null || amount === '') return null;
  const num = Number(amount);
  if (Number.isNaN(num)) return `${amount} ${symbol}`;
  return `${num.toLocaleString('fa-IR')} ${symbol}`;
};

/* ─── Status helpers ─── */
const getCallState = (call = {}, data = {}, item = {}) => {
  const event = String(
    item?.event ||
    data?.event ||
    call?.event ||
    item?.status ||
    data?.status ||
    call?.status ||
    ''
  ).toLowerCase().trim();

  const disposition = String(
    data?.disposition ||
    call?.disposition ||
    item?.disposition ||
    ''
  ).toUpperCase().trim();

  if (
    event === 'hangup' ||
    event === 'hangout' ||
    event === 'ended' ||
    event === 'completed' ||
    event === 'cancel' ||
    event === 'cancelled' ||
    event === 'failed' ||
    event === 'busy' ||
    call?.ended_at ||
    data?.ended_at ||
    item?.ended_at ||
    disposition === 'BUSY' ||
    disposition === 'FAILED' ||
    disposition === 'NO ANSWER'
  ) {
    return 'hangup';
  }

  if (
    event === 'answered' ||
    event === 'answer' ||
    event === 'talking' ||
    event === 'in_progress' ||
    event === 'in-progress' ||
    event === 'bridge' ||
    disposition === 'ANSWERED' ||
    call?.answered_at ||
    data?.answered_at ||
    item?.answered_at
  ) {
    return 'answered';
  }

  return 'ringing';
};

const STATE_CONFIG = {
  ringing: {
    key: 'ringing',
    label: 'در حال زنگ...',
    icon: <SoundOutlined className="animate-bounce" />,
    borderColor: '#10b981', // سبز زمردی واضح
    boxShadow: '0 10px 25px -4px rgba(16, 185, 129, 0.40), 0 2px 8px -2px rgba(16, 185, 129, 0.25)',
    topStrip: 'bg-emerald-500 animate-pulse',
    dot: 'bg-emerald-500 animate-ping',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
    phoneColor: 'text-emerald-600 dark:text-emerald-400',
    avatarRing: 'ring-2 ring-emerald-400/50',
    avatarBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    headerGradient: 'from-emerald-600 to-teal-700 dark:from-emerald-700 dark:to-teal-800',
    ownerBadge: 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-100/60 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200',
    ownerIcon: 'text-emerald-600 dark:text-emerald-400',
    actionText: 'text-emerald-600 dark:text-emerald-400',
  },
  answered: {
    key: 'answered',
    label: 'مکالمه جاری',
    icon: <PhoneOutlined />,
    borderColor: '#2563eb', // آبی مشخص
    boxShadow: '0 10px 25px -4px rgba(37, 99, 235, 0.40), 0 2px 8px -2px rgba(37, 99, 235, 0.25)',
    topStrip: 'bg-blue-600',
    dot: 'bg-blue-600',
    badge: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
    phoneColor: 'text-blue-600 dark:text-blue-400',
    avatarRing: 'ring-2 ring-blue-400/50',
    avatarBg: 'bg-gradient-to-br from-blue-600 to-indigo-600',
    headerGradient: 'from-blue-600 to-indigo-700 dark:from-blue-700 dark:to-indigo-800',
    ownerBadge: 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-100/60 dark:border-blue-900/40 text-blue-900 dark:text-blue-200',
    ownerIcon: 'text-blue-600 dark:text-blue-400',
    actionText: 'text-blue-600 dark:text-blue-400',
  },
  hangup: {
    key: 'hangup',
    label: 'پایان تماس',
    icon: <CheckCircleOutlined />,
    borderColor: '#ef4444', // قرمز مشخص
    boxShadow: '0 10px 25px -4px rgba(239, 68, 68, 0.40), 0 2px 8px -2px rgba(239, 68, 68, 0.25)',
    topStrip: 'bg-red-500',
    dot: 'bg-red-500',
    badge: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800',
    phoneColor: 'text-rose-600 dark:text-rose-400',
    avatarRing: 'ring-2 ring-rose-400/50',
    avatarBg: 'bg-gradient-to-br from-rose-500 to-red-600',
    headerGradient: 'from-rose-600 to-red-700 dark:from-rose-700 dark:to-red-800',
    ownerBadge: 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-100/60 dark:border-rose-900/40 text-rose-900 dark:text-rose-200',
    ownerIcon: 'text-rose-600 dark:text-rose-400',
    actionText: 'text-rose-600 dark:text-rose-400',
  },
};


const resolveOwnerName = (primary, deals = [], leads = []) => {
  if (primary?.owner_name) return primary.owner_name;
  if (primary?.owner?.name) return primary.owner.name;
  if (Array.isArray(deals) && deals.length > 0) {
    if (deals[0].owner_name) return deals[0].owner_name;
    if (deals[0].owner?.name) return deals[0].owner.name;
  }
  if (Array.isArray(leads) && leads.length > 0) {
    if (leads[0].owner_name) return leads[0].owner_name;
    if (leads[0].owner?.name) return leads[0].owner.name;
  }
  return null;
};

/**
 * ─── Collapsed Card ───
 * Minimal card showing: status, contact name, phone, owner, extension
 */
const CollapsedCard = ({ item, onExpand, onClose, callState }) => {
  const data = item.data || {};
  const call = data.call || {};
  const phone = data.phone || call.phone || '';
  const match = data.match || {};
  const primary = match.primary;
  const contactName = primary?.name || primary?.client_name || 'ناشناس';
  const primaryMeta = primary?.type ? TYPE_META[primary.type] : null;

  const cfg = STATE_CONFIG[callState] || STATE_CONFIG.ringing;
  const extension = call.extension || data.extension;

  const ownerName = useMemo(() => {
    return resolveOwnerName(primary, match.deals, match.leads);
  }, [primary, match.deals, match.leads]);

  const durationSec = data.duration ?? data.billsec ?? call.duration ?? call.billsec;
  const [talkingSeconds, setTalkingSeconds] = useState(0);

  useEffect(() => {
    if (callState === 'answered') {
      const interval = setInterval(() => {
        setTalkingSeconds((s) => s + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    setTalkingSeconds(0);
  }, [callState]);

  return (
    <div
      onClick={onExpand}
      className="pointer-events-auto w-[min(340px,calc(100vw-2rem))] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl cursor-pointer group relative overflow-hidden select-none"
      dir="rtl"
      style={{
        fontFamily: 'CustomFont',
        borderColor: cfg.borderColor,
        borderWidth: '2.5px',
        borderStyle: 'solid',
        boxShadow: cfg.boxShadow,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      {/* Status indicator strip */}
      <div className={`absolute top-0 right-0 left-0 h-1 ${cfg.topStrip}`} />

      <div className="p-3">
        {/* Row 1: Status + extension + close */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md border shrink-0 flex items-center gap-1 ${cfg.badge}`}>
              {cfg.icon}
              <span>{cfg.label}</span>
              {callState === 'answered' && talkingSeconds > 0 && (
                <span className="font-mono text-[10px] opacity-90">({formatDuration(talkingSeconds)})</span>
              )}
              {callState === 'hangup' && durationSec != null && Number(durationSec) > 0 && (
                <span className="font-mono text-[10px] opacity-90">({formatDuration(durationSec)})</span>
              )}
            </span>
            {extension && (
              <span className="text-[10px] text-gray-400 font-mono truncate" dir="ltr">
                ext: {extension}
              </span>
            )}
          </div>

          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onExpand(); }}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-0 bg-transparent cursor-pointer"
              aria-label="جزئیات"
            >
              <EyeOutlined className="text-xs" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors border-0 bg-transparent cursor-pointer"
              aria-label="بستن"
            >
              <CloseOutlined className="text-xs" />
            </button>
          </div>
        </div>

        {/* Row 2: Contact + phone */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Avatar
              size={32}
              className={`shrink-0 ${cfg.avatarBg} ${cfg.avatarRing} text-white text-xs font-bold`}
              icon={!contactName ? <UserOutlined /> : undefined}
            >
              {contactName.charAt(0)}
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="custom-font font-bold text-sm text-gray-900 dark:text-gray-100 truncate">
                  {contactName}
                </span>
                {primaryMeta && (
                  <Tag color={primaryMeta.color} className="custom-font m-0 text-[10px] px-1 py-0 leading-tight border-0">
                    {primaryMeta.label}
                  </Tag>
                )}
              </div>
            </div>
          </div>
          <span className={`font-mono font-semibold text-sm ${cfg.phoneColor} shrink-0`} dir="ltr">
            {phone || '—'}
          </span>
        </div>

        {/* Row 2.5: Responsible owner */}
        <div className={`mt-2 py-0.5 px-2 rounded-lg border flex items-center justify-between text-[11px] ${cfg.ownerBadge}`}>
          <span className="flex items-center gap-1 opacity-80">
            <UserOutlined className={`text-[10px] ${cfg.ownerIcon}`} />
            <span>مسئول:</span>
          </span>
          <span className="font-bold truncate max-w-[170px]">
            {ownerName || 'بدون مسئول تعیین‌شده'}
          </span>
        </div>

        {/* Row 3: footer hint */}
        <div className="mt-2 pt-1.5 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-400 flex items-center justify-between">
          <span className="group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex items-center gap-1">
            مشاهده جزئیات
            <ArrowLeftOutlined className="text-[9px]" />
          </span>
          {callState === 'hangup' && (
            <span className="text-rose-600 dark:text-rose-400 font-medium">ثبت نتیجه</span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ─── Expanded Panel ───
 * Full detail panel with: contact info, active deal/lead summary, outcome form, recent calls
 */
const ExpandedPanel = ({ item, onCollapse, onClose, outcomes, callState }) => {
  const navigate = useNavigate();
  const { clickToCall } = useCallCenter();

  const data = item.data || {};
  const phone = data.phone || data.call?.phone || '';
  const call = data.call || {};
  const match = useMemo(() => data.match || {}, [data.match]);
  const primary = match.primary;
  const primaryMeta = primary?.type ? TYPE_META[primary.type] : null;
  const contactName = primary?.name || primary?.client_name || 'ناشناس';
  const companyName = primary?.company_name || '';

  const cfg = STATE_CONFIG[callState] || STATE_CONFIG.ringing;

  const deals = useMemo(() => (Array.isArray(match.deals) ? match.deals : []), [match.deals]);
  const leads = useMemo(() => (Array.isArray(match.leads) ? match.leads : []), [match.leads]);

  const ownerName = useMemo(() => {
    return resolveOwnerName(primary, deals, leads);
  }, [primary, deals, leads]);

  const durationSec = data.duration ?? data.billsec ?? call.duration ?? call.billsec;
  const [talkingSeconds, setTalkingSeconds] = useState(0);

  useEffect(() => {
    if (callState === 'answered') {
      const interval = setInterval(() => {
        setTalkingSeconds((s) => s + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
    setTalkingSeconds(0);
  }, [callState]);

  // ── Outcome form state ──
  const [outcomeId, setOutcomeId] = useState(call.outcome_id || null);
  const [report, setReport] = useState(call.report || '');
  const [submittingOutcome, setSubmittingOutcome] = useState(false);

  // ── Recent calls state ──
  const [recentCalls, setRecentCalls] = useState(
    Array.isArray(data.recent_calls) ? data.recent_calls : []
  );
  const [loadingRecentCalls, setLoadingRecentCalls] = useState(false);

  // ── Edit recent report state ──
  const [editingRecentId, setEditingRecentId] = useState(null);
  const [editingRecentReport, setEditingRecentReport] = useState('');
  const [savingRecentReport, setSavingRecentReport] = useState(false);

  useEffect(() => {
    if (call.outcome_id) setOutcomeId(call.outcome_id);
    if (call.report) setReport(call.report);
  }, [call.outcome_id, call.report]);

  useEffect(() => {
    if (recentCalls.length === 0 && phone) {
      setLoadingRecentCalls(true);
      callCenterCallsApi
        .getAll({ phone, per_page: 5 })
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.data?.data || res?.data || [];
          const filtered = list.filter((c) => String(c.id) !== String(call.id));
          setRecentCalls(filtered);
        })
        .catch((err) => console.error('Failed to load recent calls', err))
        .finally(() => setLoadingRecentCalls(false));
    }
  }, [recentCalls.length, phone, call.id]);

  const handleSubmitOutcome = async () => {
    if (!call.id) {
      message.info('اطلاعات تماس جاری در دسترس نیست');
      return;
    }
    if (!outcomeId && !report.trim()) {
      message.warning('لطفاً نتیجه تماس یا یادداشت را وارد کنید');
      return;
    }
    setSubmittingOutcome(true);
    try {
      const payload = {};
      if (outcomeId != null) payload.outcome_id = outcomeId;
      if (report !== undefined) payload.report = report.trim();
      await callCenterCallsApi.update(call.id, payload);
      message.success('نتیجه تماس ثبت شد');
      onClose();
    } catch (error) {
      console.error(error);
      message.error('خطا در ثبت نتیجه تماس');
    } finally {
      setSubmittingOutcome(false);
    }
  };

  const handleSaveRecentReport = async (recentCallId) => {
    if (!recentCallId) return;
    setSavingRecentReport(true);
    try {
      const trimmed = editingRecentReport.trim();
      await callCenterCallsApi.update(recentCallId, { report: trimmed });
      message.success('یادداشت تماس به‌روزرسانی شد');
      setRecentCalls((prev) =>
        prev.map((c) => (c.id === recentCallId ? { ...c, report: trimmed } : c))
      );
      setEditingRecentId(null);
    } catch (err) {
      console.error(err);
      message.error('خطا در ذخیره یادداشت');
    } finally {
      setSavingRecentReport(false);
    }
  };

  // Active deal (first one)
  const activeDeal = deals.length > 0 ? deals[0] : null;
  // Active lead (first one, only if no deals)
  const activeLead = !activeDeal && leads.length > 0 ? leads[0] : null;

  return (
    <div
      className="pointer-events-auto w-[min(420px,calc(100vw-1.5rem))] bg-white dark:bg-gray-900 rounded-2xl flex flex-col max-h-[80vh] overflow-hidden select-text relative"
      dir="rtl"
      style={{
        fontFamily: 'CustomFont',
        borderColor: cfg.borderColor,
        borderWidth: '2.5px',
        borderStyle: 'solid',
        boxShadow: cfg.boxShadow,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      {/* ── Header ── */}
      <div className={`bg-gradient-to-l ${cfg.headerGradient} px-4 py-3 text-white shrink-0`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <Avatar
              size={36}
              className={`shrink-0 bg-white/15 text-white font-bold text-sm border border-white/20 ${cfg.avatarRing}`}
              icon={!contactName ? <UserOutlined /> : undefined}
            >
              {contactName.charAt(0)}
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-sm text-white truncate">{contactName}</span>
                {primaryMeta && (
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-md">
                    {primaryMeta.label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-white/90 text-xs font-mono" dir="ltr">{phone || '—'}</span>
                <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded-md flex items-center gap-1">
                  {cfg.icon}
                  <span>{cfg.label}</span>
                  {callState === 'answered' && talkingSeconds > 0 && (
                    <span className="font-mono">({formatDuration(talkingSeconds)})</span>
                  )}
                  {callState === 'hangup' && durationSec != null && Number(durationSec) > 0 && (
                    <span className="font-mono">({formatDuration(durationSec)})</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={onCollapse}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center border-0 text-white cursor-pointer transition-colors"
              aria-label="کوچک‌نمایی"
            >
              <DownOutlined className="text-xs" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-lg bg-white/10 hover:bg-rose-500/40 flex items-center justify-center border-0 text-white cursor-pointer transition-colors"
              aria-label="بستن"
            >
              <CloseOutlined className="text-xs" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto p-3.5 space-y-3">

        {/* Contact meta row: company + owner + extension */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
          {companyName && (
            <span className="flex items-center gap-1">
              <BankOutlined className="text-gray-400" />
              <span className="truncate max-w-[140px]">{companyName}</span>
            </span>
          )}
          {ownerName && (
            <span className="flex items-center gap-1">
              <UserOutlined className="text-teal-600 dark:text-teal-400" />
              <span>مسئول:</span>
              <span className="font-semibold text-gray-800 dark:text-gray-200">{ownerName}</span>
            </span>
          )}
          {call.extension && (
            <span className="text-gray-400" dir="ltr">
              ext: {call.extension}
            </span>
          )}
        </div>

        {/* Quick navigation buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {primaryMeta && primary?.id && (
            <Button
              type="primary"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => { navigate(primaryMeta.path(primary.id)); onClose(); }}
              className={`${PANEL_PRIMARY_BTN_CLASS} custom-font text-xs rounded-lg`}
            >
              پروفایل {primaryMeta.label}
            </Button>
          )}
          {call.id && (
            <Button
              size="small"
              onClick={() => { navigate(`/call-center/calls/${call.id}`); onClose(); }}
              className="custom-font text-xs rounded-lg"
            >
              جزئیات تماس
            </Button>
          )}
          {phone && (
            <Tooltip title="تماس مجدد" overlayStyle={{ zIndex: 10000 }}>
              <Button
                size="small"
                icon={<PhoneOutlined />}
                onClick={() => clickToCall(phone)}
                className="custom-font text-xs rounded-lg"
              />
            </Tooltip>
          )}
        </div>

        {/* Active deal or lead — compact inline */}
        {activeDeal && (
          <div className="p-2.5 rounded-xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/50 text-xs">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <DollarOutlined className="text-blue-600 dark:text-blue-400 shrink-0" />
                <span className="font-bold text-blue-900 dark:text-blue-200 truncate">
                  {activeDeal.name || 'معامله'}
                </span>
                {activeDeal.stage_name && (
                  <Tag
                    color={activeDeal.stage_color || 'blue'}
                    className="custom-font m-0 text-[10px] px-1 py-0 shrink-0 border-0"
                  >
                    {activeDeal.stage_name}
                  </Tag>
                )}
              </div>
              <Button
                size="small"
                type="link"
                icon={<LinkOutlined />}
                onClick={() => { navigate(`/deals/${activeDeal.id}`); onClose(); }}
                className="p-0 text-blue-600 dark:text-blue-400 text-[11px] h-auto custom-font"
              >
                مشاهده
              </Button>
            </div>
            <div className="flex items-center justify-between mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              <span>{formatCurrency(activeDeal.value, activeDeal.currency_symbol) || 'مبلغ نامشخص'}</span>
              {deals.length > 1 && (
                <span className="text-blue-500 font-medium">+{deals.length - 1} معامله دیگر</span>
              )}
            </div>
          </div>
        )}

        {activeLead && (
          <div className="p-2.5 rounded-xl bg-orange-50/60 dark:bg-orange-950/20 border border-orange-100 dark:border-orange-900/50 text-xs">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <UserAddOutlined className="text-orange-600 dark:text-orange-400 shrink-0" />
                <span className="font-bold text-orange-900 dark:text-orange-200 truncate">
                  {activeLead.client_name || 'سرنخ'}
                </span>
                {activeLead.status_name && (
                  <Tag
                    color={activeLead.status_color || 'orange'}
                    className="custom-font m-0 text-[10px] px-1 py-0 shrink-0 border-0"
                  >
                    {activeLead.status_name}
                  </Tag>
                )}
              </div>
              <Button
                size="small"
                type="link"
                icon={<LinkOutlined />}
                onClick={() => { navigate(`/leads/${activeLead.id}`); onClose(); }}
                className="p-0 text-orange-600 dark:text-orange-400 text-[11px] h-auto custom-font"
              >
                مشاهده
              </Button>
            </div>
            {leads.length > 1 && (
              <div className="mt-1 text-[11px] text-orange-500 font-medium">
                +{leads.length - 1} سرنخ دیگر
              </div>
            )}
          </div>
        )}

        {/* ── Outcome form ── */}
        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 p-3 space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-200">
            <FileTextOutlined className="text-teal-600 dark:text-teal-400" />
            <span>نتیجه و یادداشت تماس</span>
          </div>

          <Select
            className="w-full custom-font"
            size="small"
            placeholder="انتخاب نتیجه تماس..."
            value={outcomeId}
            onChange={setOutcomeId}
            allowClear
            options={outcomes.map((o) => ({ value: o.id, label: o.name }))}
            popupClassName="callcenter-outcome-dropdown custom-font"
            dropdownClassName="callcenter-outcome-dropdown custom-font"
            popupStyle={{ zIndex: 10000 }}
            dropdownStyle={{ zIndex: 10000 }}
            styles={{ popup: { root: { zIndex: 10000 } } }}
          />

          <TextArea
            rows={2}
            size="small"
            placeholder="خلاصه مکالمه..."
            value={report}
            onChange={(e) => setReport(e.target.value)}
            className="custom-font text-xs rounded-lg"
            maxLength={500}
          />

          <div className="flex justify-end">
            <Button
              type="primary"
              size="small"
              className={`${PANEL_PRIMARY_BTN_CLASS} custom-font text-xs rounded-lg`}
              onClick={handleSubmitOutcome}
              loading={submittingOutcome}
              disabled={!outcomeId && !report.trim()}
              icon={<CheckCircleOutlined />}
            >
              ثبت
            </Button>
          </div>
        </div>

        {/* ── Recent calls ── */}
        <div>
          <div className="flex items-center gap-1.5 mb-2 text-xs font-bold text-gray-700 dark:text-gray-200">
            <HistoryOutlined className="text-teal-600 dark:text-teal-400" />
            <span>سوابق تماس ({recentCalls.length})</span>
          </div>

          {loadingRecentCalls ? (
            <div className="text-center py-3">
              <Spin size="small" />
              <span className="custom-font text-xs text-gray-400 mr-2">دریافت سوابق...</span>
            </div>
          ) : recentCalls.length === 0 ? (
            <div className="py-2 text-center text-xs text-gray-400">
              تماس قبلی ثبت نشده
            </div>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {recentCalls.map((rc) => {
                const outcomeName = rc.outcome?.name || (typeof rc.outcome === 'string' ? rc.outcome : null);

                return (
                  <div
                    key={rc.id}
                    className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 text-xs"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-1.5 text-gray-400 text-[11px]">
                        <ClockCircleOutlined />
                        <span dir="ltr">
                          {rc.started_at
                            ? toJalali(rc.started_at, 'jYYYY/jMM/jDD HH:mm') || rc.started_at
                            : '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {outcomeName && (
                          <Tag color="blue" className="custom-font m-0 text-[10px] px-1 py-0 border-0">
                            {outcomeName}
                          </Tag>
                        )}
                        <Tag
                          color={rc.direction === 'outgoing' ? 'cyan' : 'green'}
                          className="custom-font m-0 text-[10px] px-1 py-0 border-0"
                        >
                          {rc.direction === 'outgoing' ? 'خروجی' : 'ورودی'}
                        </Tag>
                      </div>
                    </div>

                    {/* Report editing / display */}
                    {editingRecentId === rc.id ? (
                      <div className="mt-1.5 space-y-1.5">
                        <TextArea
                          rows={2}
                          size="small"
                          value={editingRecentReport}
                          onChange={(e) => setEditingRecentReport(e.target.value)}
                          className="custom-font text-xs"
                        />
                        <div className="flex justify-end gap-1">
                          <Button size="small" onClick={() => setEditingRecentId(null)}>
                            انصراف
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            loading={savingRecentReport}
                            onClick={() => handleSaveRecentReport(rc.id)}
                            className={PANEL_PRIMARY_BTN_CLASS}
                          >
                            ذخیره
                          </Button>
                        </div>
                      </div>
                    ) : rc.report ? (
                      <div className="mt-1 flex items-start justify-between gap-1 text-gray-600 dark:text-gray-300">
                        <div className="flex items-start gap-1 flex-1">
                          <CommentOutlined className="text-amber-500 mt-0.5 shrink-0 text-[11px]" />
                          <span className="text-[11px] leading-relaxed">{rc.report}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setEditingRecentId(rc.id); setEditingRecentReport(rc.report || ''); }}
                          className="text-[11px] text-amber-600 dark:text-amber-400 hover:underline bg-transparent border-0 cursor-pointer p-0 shrink-0"
                        >
                          ویرایش
                        </button>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                        <span className="italic">
                          بدون یادداشت {rc.billsec ? `(${formatDuration(rc.billsec)})` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setEditingRecentId(rc.id); setEditingRecentReport(''); }}
                          className="text-[11px] text-teal-600 hover:underline bg-transparent border-0 cursor-pointer p-0"
                        >
                          + یادداشت
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ─── SingleCallCard ───
 * Delegates to CollapsedCard or ExpandedPanel based on state
 */
const SingleCallCard = ({ item, isExpanded, onExpand, onCollapse, onClose, outcomes }) => {
  const data = item.data || {};
  const call = data.call || {};
  const callState = getCallState(call, data, item);

  if (!isExpanded) {
    return (
      <CollapsedCard
        item={item}
        onExpand={onExpand}
        onClose={onClose}
        callState={callState}
      />
    );
  }

  return (
    <ExpandedPanel
      item={item}
      onCollapse={onCollapse}
      onClose={onClose}
      outcomes={outcomes}
      callState={callState}
    />
  );
};

/**
 * ─── CallScreenPopModal ───
 * Stack manager: renders popups anchored to bottom-left
 */
const CallScreenPopModal = () => {
  const {
    screenPops,
    screenPopOpen,
    screenPopData,
    closeScreenPop,
    expandScreenPop,
    collapseScreenPop,
  } = useCallCenter();

  const [outcomes, setOutcomes] = useState([]);

  useEffect(() => {
    callCenterOutcomesApi
      .getAll()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        setOutcomes(data);
      })
      .catch((err) => console.error('Failed to load outcomes', err));
  }, []);

  const activeItems = useMemo(() => {
    if (Array.isArray(screenPops) && screenPops.length > 0) {
      return screenPops;
    }
    if (screenPopOpen && screenPopData) {
      const id = String(
        screenPopData.call_id ||
        screenPopData.call?.id ||
        screenPopData.uniqueid ||
        screenPopData.phone ||
        'single-call'
      );
      return [{ id, data: screenPopData, isExpanded: true }];
    }
    return [];
  }, [screenPops, screenPopOpen, screenPopData]);

  if (activeItems.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-4 left-4 z-[9990] flex flex-col-reverse items-start gap-2.5 pointer-events-none max-h-[calc(100vh-2rem)] overflow-y-auto no-scrollbar"
      dir="rtl"
      style={{ fontFamily: 'CustomFont', zIndex: 9990 }}
    >
      {activeItems.map((item, idx) => {
        const itemKey = item.id || item.data?.call_id || item.data?.uniqueid || item.data?.phone || idx;
        return (
          <SingleCallCard
            key={itemKey}
            item={item}
            isExpanded={Boolean(item.isExpanded)}
            onExpand={() => expandScreenPop?.(item.id)}
            onCollapse={() => collapseScreenPop?.(item.id)}
            onClose={() => closeScreenPop?.(item.id)}
            outcomes={outcomes}
          />
        );
      })}
    </div>
  );
};

export default CallScreenPopModal;
